import React, { useState, useEffect, useRef } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@/components/shared/ui';
import { 
    generateClassCertificatePdf, 
    type ClassCertificateData,
    buildCertificateCaptureElement,
    resolveCertificateIssuerDisplayImages
} from '@/lib/generateClassCertificatePdf';

interface CertificatePreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    certificateData: ClassCertificateData;
}

export default function CertificatePreviewModal({
    isOpen,
    onClose,
    certificateData,
}: CertificatePreviewModalProps) {
    const { t } = useTranslation('class');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);
    const previewContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const loadPreview = async () => {
            setIsLoadingPreview(true);
            
            // DOM이 렌더링될 때까지 ref를 재시도
            let retryCount = 0;
            const maxRetries = 10;
            
            while (!previewContainerRef.current && retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 50));
                retryCount++;
            }
            
            if (!previewContainerRef.current) {
                console.error('미리보기 컨테이너 ref를 찾을 수 없습니다 (최대 재시도 횟수 초과)');
                alert('이수증 미리보기를 표시할 수 없습니다.');
                setIsLoadingPreview(false);
                return;
            }

            try {
                console.log('이수증 미리보기 생성 시작...');
                
                // 이미지 URL 미리 로드
                const { issuerImageUrl, partnerLogoImageUrl } = await resolveCertificateIssuerDisplayImages(certificateData);
                console.log('이미지 로드 완료:', { issuerImageUrl, partnerLogoImageUrl });
                
                // 미리보기용 이수증 HTML 생성
                const previewElement = buildCertificateCaptureElement(
                    certificateData,
                    issuerImageUrl,
                    partnerLogoImageUrl,
                    { isPreview: true }
                );
                console.log('이수증 HTML 생성 완료');

                // 기존 미리보기 제거 후 추가
                if (previewContainerRef.current) {
                    previewContainerRef.current.innerHTML = '';
                    previewContainerRef.current.appendChild(previewElement);
                    console.log('미리보기 표시 완료');
                }
            } catch (error) {
                console.error('미리보기 로드 실패:', error);
                alert('이수증 미리보기 생성에 실패했습니다.');
            } finally {
                setIsLoadingPreview(false);
            }
        };

        loadPreview();
    }, [isOpen]);

    const handleDownload = async () => {
        if (isGenerating) return;
        
        try {
            setIsGenerating(true);
            await generateClassCertificatePdf(certificateData);
        } catch (error) {
            console.error(t('certificate.generateError'), error);
            alert(t('certificate.generateFailed'));
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" title={t('certificate.previewTitle')}>
            <div className="p-6">
                {/* 실제 이수증 디자인 미리보기 */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-8 mb-6 overflow-auto">
                    {isLoadingPreview && (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 size={32} className="animate-spin text-brand-500" />
                        </div>
                    )}
                    <div 
                        ref={previewContainerRef} 
                        className="w-full" 
                        style={{ display: isLoadingPreview ? 'none' : 'block' }}
                    />
                </div>

                <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={onClose} disabled={isGenerating}>
                        {t('certificate.close')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleDownload}
                        disabled={isGenerating || isLoadingPreview}
                        className="inline-flex items-center gap-2"
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                {t('certificate.generating')}
                            </>
                        ) : (
                            <>
                                <Download size={16} />
                                {t('certificate.download')}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
